import { useState, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { setTokenProvider, ApiError } from '../api/client';
import { Spinner } from '../components/Spinner';
import { authConfig, authEnabled } from './config';
import './auth.css';

// Gates the app behind Auth0 login when configured. `authEnabled` is a
// module constant, so the branch below is stable across renders (no
// conditional-hook hazard).
export function AuthGate({ children }: { children: ReactNode }) {
  if (!authEnabled) return <>{children}</>;
  return <Auth0Gate>{children}</Auth0Gate>;
}

function Auth0Gate({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();

  const [sessionExpired, setSessionExpired] = useState(false);
  const queryClient = useQueryClient();

  // Watch query and mutation caches for 401s — when the server rejects our
  // token, replace the app content with the login screen immediately rather
  // than leaving the user stranded on a "Retry" button that won't work.
  useEffect(() => {
    const is401 = (err: unknown) =>
      err instanceof ApiError && err.status === 401;

    const unsubQuery = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.query.state.status === 'error') {
        if (is401(event.query.state.error)) setSessionExpired(true);
      }
    });
    const unsubMutation = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        if (is401(event.mutation.state.error)) setSessionExpired(true);
      }
    });
    return () => {
      unsubQuery();
      unsubMutation();
    };
  }, [queryClient]);

  // Register the token getter synchronously (before any child useEffect fires).
  // useLayoutEffect runs before useEffect, so descendant effects that call the
  // API will already have a token provider set.
  useLayoutEffect(() => {
    if (isAuthenticated) {
      setTokenProvider(() =>
        getAccessTokenSilently().catch(() => null),
      );
    }
    return () => setTokenProvider(null);
  }, [isAuthenticated, getAccessTokenSilently]);

  if (isLoading) return <Spinner label="Loading…" />;

  if (error) {
    return (
      <LoginScreen
        message={`Sign-in error: ${error.message}`}
        onLogin={() => loginWithRedirect()}
      />
    );
  }

  if (!isAuthenticated || sessionExpired) {
    return (
      <LoginScreen
        message={sessionExpired ? 'Your session has expired.' : undefined}
        onLogin={() => {
          setSessionExpired(false);
          loginWithRedirect();
        }}
      />
    );
  }

  return <>{children}</>;
}

function LoginScreen({ message, onLogin }: { message?: string; onLogin: () => void }) {
  return (
    <div className="login-screen">
      <div className="login-card">
        <img className="login-logo" src="/icons/Icon-192.png" alt="" width={72} height={72} />
        <h1 className="login-title">Agenda Manager</h1>
        <p className="login-sub">Sign in to create and publish meeting agendas.</p>
        {message ? <p className="login-error">{message}</p> : null}
        <button className="btn btn-primary login-btn" onClick={onLogin}>
          Log in
        </button>
        <p className="login-tenant">{authConfig.domain}</p>
      </div>
    </div>
  );
}
